import React, { useContext, useEffect, useState } from 'react';
import UserContext from '../../../contexts/UserContext';
import { fetchResidentInterests, getDefaultInterestsModel, upsertResidentInterests } from '../../services/MyProfile';
import { listDowMapping } from '../../services/Service';
import sendToast from '../../../utils/Toast';
import { cloneDeep } from 'lodash';
import {
    ChevronIcon,
    DefaultDimensionIcon,
    EmotionalIcon,
    IntellectualIcon,
    OccupationalIcon,
    PhysicalIcon,
    SocialIcon,
    SpiritualIcon,
} from './WellnessIcons';

interface Props {
    setLoading: (loading: boolean) => void;
}

type DowColorMapping = Record<string, string>;

// Dimension icons are purely decorative and stay client-side; colors come from
// the backend's DOW color mapping (listDowMapping) so they stay in sync with
// whatever palette is configured per-facility (e.g. the calendar's wellness filter chips).
const DIMENSION_ICONS: Record<string, JSX.Element> = {
    intellectual: <IntellectualIcon />,
    occupational: <OccupationalIcon />,
    physical: <PhysicalIcon />,
    social: <SocialIcon />,
    emotional: <EmotionalIcon />,
    spiritual: <SpiritualIcon />,
};

const DEFAULT_DIMENSION_ICON = <DefaultDimensionIcon />;
const DEFAULT_DIMENSION_COLOR = '#1f3566';

const getDimensionColor = (key: string, dowColorMapping: DowColorMapping) => {
    if (dowColorMapping[key]) return dowColorMapping[key];
    const match = Object.keys(dowColorMapping).find(mappedKey => mappedKey.toLowerCase() === key.toLowerCase());
    return match ? dowColorMapping[match] : DEFAULT_DIMENSION_COLOR;
};

const isGroup = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === 'object' && !Array.isArray(value);

const ResidentInterests = ({ setLoading }: Props) => {
    const [residentInterests, setResidentInterests] = useState<any>({}); // This is the object that will be passed to the ResidentInterests component
    const [expandedDimensions, setExpandedDimensions] = useState<Record<string, boolean>>({});
    const [dowColorMapping, setDowColorMapping] = useState<DowColorMapping>({});

    const {
        userObject: {
            registrants: { list: listOfResidents, activeRegistrantsIndexes },
        },
        userObject
    } = useContext(UserContext);

    const activeResident = listOfResidents[activeRegistrantsIndexes[0]]; // get the first resident from the list of active residents because neighbors web only supports the resident who is logged in and we can't deselect or change it

    useEffect(() => {
        (async () => {
            await fetchInterests();
        })();
    }, [userObject]);

    useEffect(() => {
        (async () => {
            if (!activeResident?.Facility) return;
            try {
                const colorMapping = await listDowMapping(activeResident.Facility);
                setDowColorMapping(colorMapping);
            } catch (err) {
                sendToast('error', err instanceof Error ? err.message : 'Error fetching wellness colors');
            }
        })();
    }, [activeResident?.Facility]);

    const fetchInterests = async () => {
        try {
            setLoading(true);
            const [interests, defaultInterests] = await Promise.all([fetchResidentInterests(activeResident.Facility, String(activeResident._id)), getDefaultInterestsModel(activeResident.Facility)]);
            if (interests && defaultInterests) {
                const requiredKeys = Object.keys(defaultInterests);
                const cleanedUpInterests = {};
                requiredKeys.forEach(key => {
                    if (interests[key])
                        cleanedUpInterests[key] = interests[key];
                });
                setResidentInterests(cleanedUpInterests);
            }
        } catch (err) {
            sendToast('error', err instanceof Error ? err.message : 'Error fetching resident interests');
        } finally {
            setLoading(false);
        }
    };

    const handleChipToggle = async (itemNameKey: string, checked: boolean) => {
        try {
            setLoading(true);
            // Algo that dynamically updates the the selected key
            /*
                Example:
                if you want to update the value of "Intellectual.tech.electronics" to true
                1. Clone the residentInterests object
                2. Split the key name by '.' resulting in ["Intellectual", "tech", "electronics"]
                3. Loop through the array of keys and store the current position as reference
                4. Keep doing this until your position is an object
                5. Update the final key with the checked value
            */
            const clonedInterests = cloneDeep(residentInterests);
            const objectKeys = itemNameKey.split('.');
            let position = clonedInterests;
            const finalKey = objectKeys[objectKeys.length - 1];
            for (let i = 0; i < objectKeys.length; i++) {
                if (typeof position[objectKeys[i]] === 'object')
                    position = position[objectKeys[i]];
            }
            position[finalKey] = checked;
            const saved = await upsertInterests(clonedInterests);
            if (saved) {
                await fetchInterests();
                sendToast('success', 'Wellness preferences updated!');
            }
        } catch (err) {
            sendToast('error', err instanceof Error ? err.message : 'Error updating resident interests');
        } finally {
            setLoading(false);
        }
    };

    const upsertInterests = async (updatedInterests: any): Promise<boolean> => {
        try {
            await upsertResidentInterests(activeResident.Facility, String(activeResident._id), updatedInterests);
            return true;
        } catch (err) {
            sendToast('error', err instanceof Error ? err.message : 'Error updating resident interests');
            return false;
        }
    };

    const formatString = (str: string) => { // format the string to a human readable string
        const specialCases = {
            "tv": "TV",
            "nonFatMilk": "NonFat Milk",
            "milk": "1% Milk"
        };

        const specialCaseResult = specialCases[str.toLowerCase()];
        if (specialCaseResult) {
            return specialCaseResult;
        } else {
            // /(?=[A-Z])/) regex for uppercase lettter
            // /^\w/ regex for first letter
            return str.split(/(?=[A-Z])/).join(" ").replace(/^\w/, (c) => c.toUpperCase());
        }
    };

    const renderChip = (parentName: string, key: string, value: unknown) => {
        const itemNameKey = parentName ? `${parentName}.${key}` : key;
        const checked = !!value;
        return (
            <button
                type="button"
                key={itemNameKey}
                className={`chip${checked ? ' on' : ''}`}
                onClick={() => handleChipToggle(itemNameKey, !checked)}
            >
                <span className="chip-dot" />
                {formatString(key)}
            </button>
        );
    };

    // Dynamically renders the chips for a dimension (or nested group), same recursive
    // shape as the data (leaves become chips, nested objects become labeled sub-groups).
    const renderDimensionBody = (obj: Record<string, unknown>, parentName: string) => {
        const entries = Object.entries(obj);
        const leafEntries = entries.filter(([, value]) => !isGroup(value));
        const groupEntries = entries.filter(([, value]) => isGroup(value));

        return (
            <>
                {leafEntries.length > 0 && (
                    <div className="chips">
                        {leafEntries.map(([key, value]) => renderChip(parentName, key, value))}
                    </div>
                )}
                {groupEntries.map(([key, value]) => {
                    const itemNameKey = parentName ? `${parentName}.${key}` : key;
                    return (
                        <div key={itemNameKey}>
                            <div className="chip-group-label">{formatString(key)}</div>
                            {renderDimensionBody(value as Record<string, unknown>, itemNameKey)}
                        </div>
                    );
                })}
            </>
        );
    };

    const toggleDimension = (key: string) => {
        setExpandedDimensions(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const renderDimensions = () => {
        return Object.entries(residentInterests).map(([key, value]) => {
            const icon = DIMENSION_ICONS[key.toLowerCase()] || DEFAULT_DIMENSION_ICON;
            const color = getDimensionColor(key, dowColorMapping);
            const isOpen = !!expandedDimensions[key];
            return (
                <div className={`wellness-acc${isOpen ? ' open' : ''}`} key={key}>
                    <button type="button" className="wellness-trigger" onClick={() => toggleDimension(key)}>
                        <div className="wellness-w-icon" style={{ background: `color-mix(in srgb, ${color} 16%, white)`, color }}>{icon}</div>
                        <span className="wellness-acc-title">{formatString(key)}</span>
                        <div className="wellness-chevron">
                            <ChevronIcon size={16} />
                        </div>
                    </button>
                    <div className="wellness-body">
                        <div className="wellness-inner">
                            {renderDimensionBody(value as Record<string, unknown>, key)}
                        </div>
                    </div>
                </div>
            );
        });
    };

    return (
        <div className="wellness-list">
            {renderDimensions()}
        </div>
    );
};

export default ResidentInterests;
